import { IntakeFormResponseName, QuoteFormResponseName } from '@common/constants/formResponseNames';
import type { TemplateFormField, TemplateFormFieldUpdate } from '@common/types/templateFormFields';
import { getMatterTypeLabel } from '@common/util';
import AddIcon from '@mui/icons-material/Add';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    List,
    ListItem,
    type SxProps,
    TextField,
    type Theme,
    Typography,
    useTheme,
} from '@mui/material';
import { produce } from 'immer';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import BuilderFormComponent from '@/components/FormComponent/BuilderFormComponent';
import FullScreenLoader from '@/components/FullScreenLoader';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import LawFirmLogo from '@/components/LawFirmLogo';
import NewQuestionsDrawer from '@/components/NewQuestionsDrawer/NewQuestionsDrawer';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import {
    createTemplateForm,
    deleteFormField,
    updateFormFields,
    updateOrder,
    updateTemplateForm,
} from '@/services/TemplateFormsService';
import useQuoteIntakeSetupStore, {
    type InitialSetupStateType,
} from '@/stores/useQuoteIntakeSetupStore';
import { findConditionalQuestions, getLastQuestionOrder } from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

export default function QuoteDesign() {
    const [showTip, setShowTip] = useState<boolean>(true);
    const [showTip1, setShowTip1] = useState<boolean>(true);
    const [
        formData,
        activeQuote,
        setFormData,
        fetchQuoteTemplateDataOnEveryStep,
        updateStep1AfterReorder,
        preparePayloadForStep1,
    ] = useQuoteIntakeSetupStore((state) => [
        state.formData,
        state.activeQuote,
        state.setFormData,
        state.fetchQuoteTemplateDataOnEveryStep,
        state.updateStep1AfterReorder,
        state.preparePayloadForStep1,
    ]);
    const theme = useTheme();
    const { quoteIntakeId } = useParams();
    const [isFullScreenLoading, setIsFullScreenLoading] = useState(false);
    const [isQuestionDrawerOpen, setIsQuestionDrawerOpen] = useState<boolean>(false);
    const [isQuestionDrawerLoading, setIsQuestionDrawerLoading] = useState<boolean>(false);
    const [questionDrawerField, setQuestionDrawerField] = useState<TemplateFormField | undefined>(
        undefined,
    );
    const [deleteAlert, setDeleteAlert] = useState<{
        isOpen: boolean;
        field: TemplateFormField | undefined;
    }>({ isOpen: false, field: undefined });
    const [isDeleteFieldLoading, setIsDeleteFieldLoading] = useState<boolean>(false);
    const firstSetupPostAfterGet = useRef<boolean>(true);
    const { activeLawFirmId } = useLawFirmContext();
    const lawFirmId = activeQuote.lawFirmId ?? activeLawFirmId;

    const quoteDesignContainerStyle: SxProps<Theme> = {
        display: 'flex',
        gap: '16px',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 168px)',
        overflow: 'auto',
    };

    const quoteDesignSectionStyle: SxProps<Theme> = {
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        height: 'fit-content',
        marginX: '32px',
    };

    const quoteDesignTextFieldStyle = {
        marginTop: '16px',
        width: '100%',
        fontSize: '14px',
    };

    const quoteDesignTextAreaStyles: SxProps<Theme> = {
        ...quoteDesignTextFieldStyle,
        '& .MuiOutlinedInput-root': {
            height: 'auto',
            padding: '8px 12px',
        },
    };
    // Methods
    const callUpdateFieldsApi = async (
        fieldName: string,
        formData: InitialSetupStateType['formData'],
    ) => {
        const templateId = quoteIntakeId || activeQuote.quoteTemplateId;
        if (templateId) {
            const templateFormId = formData.step1.templateFormId;
            const formField = formData.step1.templateFormFields.find((f) => f.name === fieldName);
            if (formField) {
                const templateFormFieldId = formField.templateFormFieldId;
                const payload = {
                    title: formField.title,
                    name: formField.name || formField.questionSchema.name, // If name is not available on formField
                    subTitle: !isEmpty(formField.questionSchema.description)
                        ? formField.questionSchema.description
                        : 'NA',
                    questionSchema: formField.questionSchema,
                    questionValidationSchema: formField.questionValidationSchema,
                    displayOrder: formField.displayOrder,
                    isVisibleToUser: formField.isVisibleToUser,
                    isLocked: formField.isLocked,
                    isRequiredForFullAutomation: formField.isRequiredForFullAutomation,
                };

                if (templateFormId && templateFormFieldId) {
                    try {
                        await updateFormFields(
                            templateId,
                            templateFormId,
                            templateFormFieldId,
                            payload,
                        );
                    } catch (error: any) {
                        console.error(error);
                        throw error;
                    }
                }
            }
        }
    };

    const handleValueChange = (key: string, value: string | File | undefined): void => {
        const updatedStep1 = produce(formData, (draftFormData) => {
            const currentStep1 = draftFormData.step1;
            currentStep1[key] = value;
        });
        setFormData('step1', updatedStep1.step1);
    };

    const onEditInitHandler = (formData: TemplateFormField) => {
        setIsQuestionDrawerOpen(true);
        setQuestionDrawerField(formData);
    };

    const setNewOrder = async (
        order: {
            originalOrder: number;
            newOrder: number;
        },
        field: TemplateFormField,
    ) => {
        // check if the templateFormId exist

        // prepare the payload and be ready to call the API
        // required ids to make the call quoteTemplateId, templateFormId and templateFormFieldId
        const payload = {
            preOrder: order.originalOrder,
            newOrder: order.newOrder,
        };

        const quoteTemplateId =
            activeQuote?.quoteTemplateId || formData.step1.apiResponse?.quoteTemplateId;
        const templateFormId = formData.step1.apiResponse?.templateFormId;
        const templateFormFieldId = field?.templateFormFieldId;

        // API Operations
        if (quoteTemplateId && templateFormId && templateFormFieldId) {
            try {
                // call the api to update the order of the field
                const updateOrderResp = await updateOrder(
                    quoteTemplateId,
                    templateFormId,
                    templateFormFieldId,
                    payload,
                );
                if (updateOrderResp) {
                    updateStep1AfterReorder(updateOrderResp);
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    //Updates the visibility of the question in the store
    const setVisibility = async (isVisible: boolean, fieldName: string) => {
        const updatedStep1 = produce(formData, (draftFormData) => {
            // create a copy of step1
            const currentStep1 = { ...draftFormData.step1 };

            // update templateformFields array
            currentStep1.templateFormFields = currentStep1.templateFormFields.map((field) => {
                if (fieldName === field.name) {
                    return { ...field, isVisibleToUser: isVisible };
                } else {
                    return field;
                }
            });

            // update step1 property in draft
            draftFormData.step1 = currentStep1;
        });

        setFormData('step1', updatedStep1.step1);
        try {
            await callUpdateFieldsApi(fieldName, updatedStep1);
            successSnackbar('Form field updated successfully');
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data || 'Something went wrong');
        }
    };

    //fetch quote design api
    const fetchData = async () => {
        // Todo: Add new condition here to not call the fetch API
        // if the matter has been modified on the first step of the setup
        // to identify it we have to check the apiResponse of the step0 and
        // match the matter type coming in the get API response and the matter type in state
        // if the type has been changed on the first step then don't call the get API for quote response
        if ((quoteIntakeId || activeQuote?.quoteTemplateId) && !formData.step1.apiResponse) {
            setIsFullScreenLoading(true);
            try {
                await fetchQuoteTemplateDataOnEveryStep(
                    'step1',
                    quoteIntakeId || activeQuote?.quoteTemplateId,
                );
            } catch (error) {
                console.error(error);
                // check if the status is 404
                if (error.status === 404) {
                    // call the post API if that is the case
                    // prepare the payload
                    if (firstSetupPostAfterGet.current) {
                        firstSetupPostAfterGet.current = false;
                        const payload = preparePayloadForStep1();
                        try {
                            // POST
                            const templateFormResp = await createTemplateForm(
                                activeQuote?.quoteTemplateId || quoteIntakeId,
                                payload,
                            );
                            // call the get API again here to update the
                            // rendered schema to make the re-order work properly
                            // check if the POST was succesful
                            if (templateFormResp.status === 201) {
                                // Get API call
                                try {
                                    await fetchQuoteTemplateDataOnEveryStep('step1', quoteIntakeId);
                                } catch (error) {
                                    console.error(error);
                                }
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }
            } finally {
                setIsFullScreenLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [quoteIntakeId]);

    //Add new question
    const handleConfirmAddNewField = async (
        fieldData: TemplateFormFieldUpdate | TemplateFormFieldUpdate[],
    ) => {
        if (questionDrawerField && !Array.isArray(fieldData)) {
            //Check that the question is empty or not
            if (!fieldData?.title.trim() || fieldData?.title.trim() === '') {
                errorSnackbar('Question cannot be empty!');
                return;
            }

            //Check that the question need to be added is already present or not
            const similarQuestion = formData.step1.templateFormFields?.find((que) => {
                const questionSchema = fieldData.questionSchema;
                if (
                    questionSchema !== undefined &&
                    que.questionSchema.conditionValue !== undefined
                ) {
                    return (
                        que.title === fieldData.title &&
                        que.name !== fieldData.name &&
                        que.questionSchema.conditionValue === questionSchema.conditionValue
                    );
                } else {
                    return que.title === fieldData.title && que.name !== fieldData.name;
                }
            });
            // Return from the function with an error
            if (similarQuestion) {
                errorSnackbar('This question already exists!');
                return;
            }
            //Edit mode
            const templateId = activeQuote.quoteTemplateId;
            const templateFormId = formData.step1.templateFormId;
            if (templateId && templateFormId && fieldData.templateFormFieldId) {
                setIsQuestionDrawerLoading(true);
                try {
                    await updateFormFields(
                        templateId,
                        templateFormId,
                        fieldData.templateFormFieldId,
                        {
                            ...fieldData,
                            subTitle: fieldData.subTitle ? fieldData.subTitle : 'NA',
                        },
                    );

                    const updatedFormData = produce(formData, (draftFormData) => {
                        const currentStep1 = { ...draftFormData.step1 };
                        currentStep1.templateFormFields = currentStep1.templateFormFields.map(
                            (field) => {
                                if (fieldData.name === field.name) {
                                    return {
                                        ...field,
                                        ...fieldData,
                                    };
                                } else {
                                    return field;
                                }
                            },
                        );
                        draftFormData.step1 = currentStep1;
                    });
                    setFormData('step1', updatedFormData.step1);
                    setIsQuestionDrawerLoading(false);
                    setIsQuestionDrawerOpen(false);
                    setQuestionDrawerField(undefined);
                    successSnackbar('Form field updated successfully');
                } catch (error) {
                    setIsQuestionDrawerLoading(false);
                    console.error(error);
                }
            }
            return;
        }
        if (!Array.isArray(fieldData)) {
            return;
        }
        const newFields = [...fieldData];
        //Adding new question
        //Check that the question need to be added is already present or not
        const similarQuestion =
            newFields.length === 1
                ? formData.step1.templateFormFields?.find((que) => {
                      if (
                          newFields[0]?.questionSchema.conditionValue !== undefined &&
                          que.questionSchema.conditionValue !== undefined
                      ) {
                          return (
                              que.title === newFields[0]?.title &&
                              que.name !== newFields[0]?.name &&
                              que.questionSchema.conditionValue ===
                                  newFields[0]?.questionSchema.conditionValue
                          );
                      } else {
                          return (
                              que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                          );
                      }
                  })
                : formData.step1.templateFormFields.find((que) => {
                        if (
                            newFields[0]?.questionSchema.conditionValue !== undefined &&
                            que.questionSchema.conditionValue !== undefined
                        ) {
                            return (
                                que.title === newFields[0]?.title &&
                                que.name !== newFields[0]?.name &&
                                que.questionSchema.conditionValue ===
                                    newFields[0]?.questionSchema.conditionValue
                            );
                        } else {
                            return (
                                que.title === newFields[0]?.title && que.name !== newFields[0]?.name
                            );
                        }
                    })
                  ? newFields.slice(1).some((field) =>
                        formData.step1.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    )
                  : newFields.some((field) =>
                        formData.step1.templateFormFields?.find((que) => {
                            const questionSchema = field.questionSchema;
                            if (
                                questionSchema.conditionValue !== undefined &&
                                que.questionSchema.conditionValue !== undefined
                            ) {
                                return (
                                    que.title === field.title &&
                                    que.name !== field.name &&
                                    que.questionSchema.conditionValue ===
                                        questionSchema.conditionValue
                                );
                            } else {
                                return que.title === field.title && que.name !== field.name;
                            }
                        }),
                    );
        // Return from the function with an error
        if (similarQuestion) {
            errorSnackbar('This question already exists!');
            return;
        }

        let result;
        if (activeQuote.quoteTemplateId && formData.step1.templateFormId) {
            if (questionDrawerField?.templateFormFieldId) {
                result = findConditionalQuestions(
                    formData.step1.templateFormFields,
                    questionDrawerField?.templateFormFieldId,
                );

                if (result?.length > 0) {
                    const modifiedResult = result.map((item) => {
                        return {
                            ...item,
                            displayOrder: newFields[0].displayOrder,
                            questionSchema: {
                                ...item.questionSchema,
                                order: newFields[0].displayOrder,
                            },
                            questionValidationSchema: item.questionValidationSchema,
                        };
                    });
                    newFields.push(...modifiedResult);
                }
            }
            setIsQuestionDrawerLoading(true);
            try {
                const payload = {
                    templateFormType: 'QuoteForm',
                    headerDescription: formData.step1.headerDescription,
                    footerTemplate: formData.step1.footerTemplate,
                    templateFormFields: newFields,
                };

                const updateTemplateResp = await updateTemplateForm(
                    activeQuote.quoteTemplateId,
                    formData.step1.templateFormId,
                    payload,
                );

                const allNames = updateTemplateResp.data.templateFormFields.map(
                    (field) => field.name,
                );

                const updatedResp = formData.step1.templateFormFields.filter(
                    (f) => !allNames.includes(f.name),
                );

                setFormData('step1', {
                    ...formData.step1,
                    templateFormFields: [
                        ...updatedResp,
                        ...updateTemplateResp.data.templateFormFields.map((field) => {
                            return {
                                ...field,
                            };
                        }),
                    ].sort((a, b) => a.displayOrder - b.displayOrder),
                });
                successSnackbar(
                    result?.length > 0 ? 'Form field updated successfully' : 'New question added',
                );

                setIsQuestionDrawerLoading(false);
                setIsQuestionDrawerOpen(false);
                setQuestionDrawerField(undefined);
                return;
            } catch (error) {
                console.error(error);
                setIsQuestionDrawerLoading(false);
                return;
            }
        }

        setFormData('step1', {
            ...formData.step1,
            templateFormFields: [...formData.step1.templateFormFields, ...newFields],
        });
        successSnackbar('New question added');
    };

    //Deleting custom question
    const onFieldDelete = async (field: TemplateFormField) => {
        // Enhanced delete logic for conditional questions
        if (field.templateFormFieldId && activeQuote.quoteTemplateId) {
            setIsDeleteFieldLoading(true);
            try {
                const conditions = field?.questionSchema?.conditions;
                // If field has conditions array with length > 0
                if (Array.isArray(conditions) && conditions.length > 0) {
                    // Find all conditional questions
                    const templateFormFields = formData.step1.templateFormFields;
                    const conditionalQuestions = findConditionalQuestions(
                        templateFormFields,
                        field.templateFormFieldId,
                    ) as Array<{ templateFormFieldId: string; name: string }>;
                    // Delete all conditional questions
                    for (const q of conditionalQuestions) {
                        if (q.templateFormFieldId) {
                            await deleteFormField(
                                activeQuote.quoteTemplateId,
                                q.templateFormFieldId,
                            );
                        }
                    }
                    // Delete the parent question itself
                    await deleteFormField(activeQuote.quoteTemplateId, field.templateFormFieldId);
                    // Remove all deleted questions from local state
                    const namesToDelete = [field.name, ...conditionalQuestions.map((q) => q.name)];
                    const updatedStep1 = produce(formData, (draftFormData) => {
                        const currentStep1 = { ...draftFormData.step1 };
                        currentStep1.templateFormFields = currentStep1.templateFormFields.filter(
                            (f) => !namesToDelete.includes(f.name),
                        );
                        draftFormData.step1 = currentStep1;
                    });
                    setFormData('step1', updatedStep1.step1);
                    setIsDeleteFieldLoading(false);
                    setDeleteAlert({ isOpen: false, field: undefined });
                    successSnackbar(
                        `Field "${field?.title}" and its conditional questions deleted!`,
                    );
                } else {
                    // ...existing code...
                    await deleteFormField(activeQuote.quoteTemplateId, field.templateFormFieldId);
                    // Remove the field from local state and from parent's conditions array (immer draft mutation)
                    const conditionedBy = field.questionSchema.conditionedBy;
                    const updatedStep1 = produce(formData, (draftFormData) => {
                        const currentStep1 = { ...draftFormData.step1 };
                        // If the field is a conditional question, find the parent and remove it from conditions
                        if (field.questionSchema.isConditionalQuestion && conditionedBy) {
                            const parentQuestion = currentStep1.templateFormFields.find(
                                (f) => f.name === conditionedBy,
                            );
                            if (
                                parentQuestion &&
                                Array.isArray(parentQuestion.questionSchema.conditions)
                            ) {
                                parentQuestion.questionSchema.conditions =
                                    parentQuestion.questionSchema.conditions.filter(
                                        (condition) => condition.nextQuestion?.name !== field.name,
                                    );
                            }
                        }
                        currentStep1.templateFormFields = currentStep1.templateFormFields.filter(
                            (f) => f.name !== field.name,
                        );
                        draftFormData.step1 = currentStep1;
                    });
                    // If parent needs to be updated, do it here using the updated state (not draft)
                    if (
                        field.questionSchema.isConditionalQuestion &&
                        conditionedBy &&
                        activeQuote.quoteTemplateId &&
                        formData.step1.templateFormId
                    ) {
                        const parentQuestionToUpdate = updatedStep1.step1.templateFormFields.find(
                            (f) => f.name === conditionedBy,
                        );
                        if (parentQuestionToUpdate?.templateFormFieldId) {
                            await updateFormFields(
                                activeQuote.quoteTemplateId,
                                formData.step1.templateFormId,
                                parentQuestionToUpdate.templateFormFieldId,
                                {
                                    ...parentQuestionToUpdate,
                                    subTitle: parentQuestionToUpdate.subTitle
                                        ? parentQuestionToUpdate.subTitle
                                        : 'NA',
                                },
                            );
                        }
                    }
                    setFormData('step1', updatedStep1.step1);
                    setIsDeleteFieldLoading(false);
                    setDeleteAlert({ isOpen: false, field: undefined }); //Close the delete alert box
                    successSnackbar(`Field "${field?.title}" deleted!`);
                }
            } catch (error) {
                console.error(error);
                setIsDeleteFieldLoading(false);
            }
        }
    };

    return (
        <Box sx={quoteDesignContainerStyle}>
            {isFullScreenLoading && <FullScreenLoader />}
            <Box
                sx={{
                    ...quoteDesignSectionStyle,
                    borderTop: `16px solid ${theme.palette.primary.P_450}`,
                    marginTop: '32px',
                    gap: '24px',
                    padding: '16px 32px 32px 32px',
                }}
            >
                {showTip && (
                    <Alert
                        icon={<InfoSolid />}
                        severity="info"
                        onClose={() => {
                            setShowTip(false);
                        }}
                    >
                        <AlertTitle>Disclaimer</AlertTitle>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Please note the form below is an example of what your future clients
                            will see. To make it complete you have to fill:
                        </Typography>
                        <List
                            sx={{
                                listStyleType: 'disc',
                                pl: 2,
                                ml: 2,
                                '& .MuiListItem-root': {
                                    display: 'list-item',
                                },
                            }}
                        >
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    form description, which clients will see at the top of your
                                    form;
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ padding: 0, margin: 0 }}>
                                <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                    ending text, which clients will see after submitting the form;
                                </Typography>
                            </ListItem>
                        </List>
                        <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                            Customize, reorder, or hide questions to tailor the form to your needs.
                        </Typography>
                    </Alert>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '32px',
                        alignItems: 'flex-start',
                    }}
                >
                    <LawFirmLogo lawFirmId={lawFirmId?.toString()} />
                    <Box sx={{ height: '100%', width: '100%' }}>
                        <Typography variant="h1">
                            {`${getMatterTypeLabel(formData.step0.transactionType)} Quote Form`}
                        </Typography>
                        <TextField
                            label="Form description"
                            multiline
                            rows={4}
                            sx={quoteDesignTextAreaStyles}
                            value={formData.step1.headerDescription}
                            error={formData.step1?.headerDescription?.length > 4000}
                            helperText={
                                formData.step1?.headerDescription?.length <= 4000
                                    ? `${formData.step1.headerDescription.length}/4000`
                                    : 'Exceeded character limit!'
                            }
                            name="headerDescription"
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                            ) => handleValueChange(event.target.name, event.target.value)}
                        />
                    </Box>
                </Box>
            </Box>
            <Box sx={{ ...quoteDesignSectionStyle }}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => {
                            setIsQuestionDrawerOpen(true);
                            setQuestionDrawerField(undefined);
                        }}
                    >
                        <AddIcon /> Add Question
                    </Button>
                </Box>
                <Box>
                    {formData.step1.templateFormFields?.length > 0 &&
                        formData.step1.templateFormFields
                            ?.filter(
                                (field) =>
                                    !field.questionSchema.isConditionalQuestion &&
                                    field.name ===
                                        IntakeFormResponseName.PURCHASERS_LISTED_ON_PURCHASE_AND_SALE_AGREEMENT,
                            )
                            .map((field, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', order: field.displayOrder }}
                                >
                                    <BuilderFormComponent
                                        fieldObj={field}
                                        lawFirmId={lawFirmId as number}
                                        allQuestions={formData.step1.templateFormFields}
                                        showSelectBoxValue={true}
                                        disabled={true}
                                        isVisible={field.isVisibleToUser}
                                        isLocked={field.isLocked}
                                        onOrderChange={(order) => {
                                            setNewOrder(order, field);
                                        }}
                                        onVisibilityChange={(isVisible: boolean) => {
                                            setVisibility(isVisible, field.name);
                                        }}
                                        formGroupIndex={0}
                                        onEditClick={(fieldObj) => onEditInitHandler(fieldObj)}
                                        onDeleteClick={(field) =>
                                            setDeleteAlert({ isOpen: true, field: field })
                                        }
                                    />
                                </div>
                            ))}
                </Box>
                <Box
                    sx={{
                        borderRadius: '8px',
                        border: `1px solid ${theme.palette.neutral.borderColor}`,
                        backgroundColor: 'neutral.white',
                        padding: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0px',
                        height: 'fit-content',
                        marginX: '32px',
                    }}
                >
                    {showTip1 && (
                        <Alert
                            icon={<InfoSolid />}
                            severity="info"
                            onClose={() => {
                                setShowTip1(false);
                            }}
                        >
                            <AlertTitle>Note</AlertTitle>
                            <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                All questions below will repeat for every{' '}
                                {formData.step0.transactionType.toLowerCase() === 'purchase'
                                    ? 'purchaser'
                                    : formData.step0.transactionType.toLowerCase() === 'sale'
                                      ? 'seller'
                                      : formData.step0.transactionType.toLowerCase() === 'refinance'
                                        ? 'borowwer'
                                        : formData.step0.transactionType.toLowerCase() ===
                                            'privatelender'
                                          ? 'lender'
                                          : ''}{' '}
                                selected in the above question. For example, if the client selects 2{' '}
                                {formData.step0.transactionType.toLowerCase() === 'purchase'
                                    ? 'purchasers'
                                    : formData.step0.transactionType.toLowerCase() === 'sale'
                                      ? 'sellers'
                                      : formData.step0.transactionType.toLowerCase() === 'refinance'
                                        ? 'borowwers'
                                        : formData.step0.transactionType.toLowerCase() ===
                                            'privatelender'
                                          ? 'lenders'
                                          : ''}
                                , the below question set will repeat twice.
                            </Typography>
                        </Alert>
                    )}
                    {formData.step1.templateFormFields?.length > 0 &&
                        formData.step1.templateFormFields
                            ?.filter(
                                (field) =>
                                    field.name === QuoteFormResponseName.FIRST_NAME ||
                                    field.name === QuoteFormResponseName.MIDDLE_NAME ||
                                    field.name === QuoteFormResponseName.LAST_NAME ||
                                    field.name === QuoteFormResponseName.EMAIL ||
                                    field.name === QuoteFormResponseName.PRIMARY_PHONE,
                            )
                            .map((field, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', order: field.displayOrder }}
                                >
                                    <BuilderFormComponent
                                        fieldObj={field}
                                        lawFirmId={lawFirmId as number}
                                        allQuestions={formData.step1.templateFormFields}
                                        showSelectBoxValue={true}
                                        disabled={true}
                                        isVisible={field.isVisibleToUser}
                                        isLocked={field.isLocked}
                                        onOrderChange={(order) => {
                                            setNewOrder(order, field);
                                        }}
                                        onVisibilityChange={(isVisible: boolean) => {
                                            setVisibility(isVisible, field.name);
                                        }}
                                        formGroupIndex={0}
                                        onEditClick={(fieldObj) => onEditInitHandler(fieldObj)}
                                        onDeleteClick={(field) =>
                                            setDeleteAlert({ isOpen: true, field: field })
                                        }
                                    />
                                </div>
                            ))}
                </Box>
                <Box>
                    {formData.step1.templateFormFields?.length > 0 &&
                        formData.step1.templateFormFields
                            ?.filter(
                                (field) =>
                                    !field.questionSchema.isConditionalQuestion &&
                                    field.name !== 'firstName' &&
                                    field.name !== 'middleName' &&
                                    field.name !== 'lastName' &&
                                    field.name !== 'email' &&
                                    field.name !== 'primaryPhone' &&
                                    field.name !== 'purchasersListedOnPurchaseAndSaleAgreement',
                            )
                            .map((field, index) => (
                                <div
                                    key={index}
                                    style={{ display: 'flex', order: field.displayOrder }}
                                >
                                    <BuilderFormComponent
                                        fieldObj={field}
                                        lawFirmId={lawFirmId as number}
                                        allQuestions={formData.step1.templateFormFields}
                                        showSelectBoxValue={true}
                                        disabled={true}
                                        isVisible={field.isVisibleToUser}
                                        isLocked={field.isLocked}
                                        onOrderChange={(order) => {
                                            setNewOrder(order, field);
                                        }}
                                        onVisibilityChange={(isVisible: boolean) => {
                                            setVisibility(isVisible, field.name);
                                        }}
                                        formGroupIndex={0}
                                        onEditClick={(fieldObj) => onEditInitHandler(fieldObj)}
                                        onDeleteClick={(field) =>
                                            setDeleteAlert({ isOpen: true, field: field })
                                        }
                                    />
                                </div>
                            ))}
                </Box>
            </Box>
            <Box sx={{ ...quoteDesignSectionStyle, gap: '0px', marginBottom: '32px' }}>
                <Typography variant="body1">
                    Please enter the ending text that clients will see after submitting the form
                </Typography>
                <TextField
                    placeholder="“Thank you for submitting the form. Our lawyers will contact you within two business days.”"
                    multiline
                    rows={3}
                    sx={quoteDesignTextAreaStyles}
                    name="footerTemplate"
                    value={formData.step1.footerTemplate}
                    error={formData.step1.footerTemplate.length > 10000}
                    helperText={
                        formData.step1.footerTemplate.length <= 10000
                            ? `${formData.step1.footerTemplate.length}/10000`
                            : 'Exceeded character limit!'
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                        handleValueChange(event.target.name, event.target.value)
                    }
                />
            </Box>
            <NewQuestionsDrawer
                isOpen={isQuestionDrawerOpen}
                onClose={() => {
                    setIsQuestionDrawerOpen(false);
                    setQuestionDrawerField(undefined);
                }}
                onConfirm={handleConfirmAddNewField}
                isLoading={isQuestionDrawerLoading}
                previousQuestionOrder={getLastQuestionOrder(formData.step1.templateFormFields)}
                formGroup={1}
                formGroupTitle=""
                formField={questionDrawerField}
            />
            <AlertDialog
                open={deleteAlert.isOpen}
                handleClose={() => setDeleteAlert({ isOpen: false, field: undefined })}
                title={'Are you sure you want to delete this custom question?'}
                message="This action cannot be undone."
                handleConfirm={() => onFieldDelete(deleteAlert.field)}
                isLoading={isDeleteFieldLoading}
            />
        </Box>
    );
}
